// Maximum depth for derived rate nesting (base → derived → derived-of-derived...).
// MUST match the SCSS loop bounds in ratePlansDerivedRate.scss:
//   - Line 235: `@for $i from 2 through 5` (depth-2+ leading bar color)
//   - Line 251: `@for $i from 1 through 5` (depth-N row indentation)
//   - Line 322: `@for $d from 0 through 4` (ancestor rail positions)
// If this changes, update all three SCSS loops.
export const MAX_DEPTH_CLASS = 5;

// Display cap: 4 levels total — base (depth 0) + 3 derived levels (depths
// 1-3). Rows deeper than this are omitted from the UI trees (pass as
// `maxDepth` to buildFlatTree) but stay in the uncapped Excel-export tree.
export const MAX_VISIBLE_DEPTH = 3;

export const ROW_KIND = {
  BASE: "BASE",
  DERIVED: "DERIVED",
};

export const COMMERCIAL_MODEL = {
  COMMUNITY: "COMMUNITY",
  DIRECT: "DIRECT",
  EXCLUSIVE: "EXCLUSIVE",
};

// distributionConfig.distribution on the metadata promo contract. Shares
// COMMERCIAL_MODEL's values but is a separate thing: that one labels how a rate is
// sold, this one is a field metadata validates on the way in — it only accepts
// EXCLUSIVE for a BUYER_CAMPAIGN promo. Keep them apart so tightening one list
// does not silently move the other.
export const DISTRIBUTION = {
  COMMUNITY: "COMMUNITY",
  DIRECT: "DIRECT",
  EXCLUSIVE: "EXCLUSIVE",
  // Prebuy inventory. A distribution value only — it is deliberately NOT a
  // COMMERCIAL_MODEL member (commercialModel stays COMMUNITY | DIRECT).
  PREBUY: "PREBUY",
};

// distributionInheritance.source on a derived rate: INHERITED means the rate
// carries no distributionConfig of its own and mirrors its parent's
// distribution + client list; OVERRIDDEN means distributionConfig is present.
export const INHERITANCE_SOURCE = {
  INHERITED: "INHERITED",
  OVERRIDDEN: "OVERRIDDEN",
};

export const PRICE_TYPE = {
  PERCENT: "PERCENT",
  FLAT: "FLAT",
};

export const PRICE_OPERATION = {
  ADDITION: "ADDITION",
  SUBTRACTION: "SUBTRACTION",
};

export const DERIVED_FROM_TYPE = {
  BASE_RATEPLAN: "BASE_RATEPLAN",
  DERIVED_RATE: "DERIVED_RATE",
};

export const DERIVED_RATE_STATUS = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
};

export const PMS_CRS_LABEL = "PMS/CRS";

// Shared with Taxes & Fees — re-exported so existing DR imports keep working.
export {
  STATUS_FILTER,
  STATUS_FILTER_OPTIONS,
} from "../../../constants/statusFilter";

import { STATUS_FILTER as SHARED_STATUS_FILTER } from "../../../constants/statusFilter";

// The Rate Plans page opens on Active only — inactive plans clutter the list
// and bury the ones suppliers actually work with. This is deliberately NOT the
// shared statusFilter default: Taxes & Fees still opens on Active & Inactive.
export const DEFAULT_STATUS_FILTER = SHARED_STATUS_FILTER.ACTIVE;

export const DISTRIBUTION_FILTER = {
  ALL: "all",
  COMMUNITY: "community",
  DIRECT: "direct",
  EXCLUSIVE: "exclusive",
  // Lowercase like its siblings; rowMatchesFilters uppercases both sides
  // before comparing (utils.js), so this matches a "PREBUY" row value.
  PREBUY: "prebuy",
};

export const POLICY_FILTER_ALL = "all";

export const POLICY_FILTER = {
  ALL: POLICY_FILTER_ALL,
  REFUNDABLE: "Refundable",
  NON_REFUNDABLE: "Non-refundable",
};

export const CANCELLATION_FILTER_OPTIONS = [
  { label: "All Cancellation Policies", value: POLICY_FILTER.ALL },
  { label: "Refundable", value: POLICY_FILTER.REFUNDABLE },
  { label: "Non-refundable", value: POLICY_FILTER.NON_REFUNDABLE },
];

// The supplier-facing word for PREBUY, declared once and consumed by both the
// filter option below and DISTRIBUTION_LABELS, so the CheckPicker chip, the
// badge and the Excel cell physically cannot drift apart.
const PREBUY_LABEL = "Prebuy";

// Multi-select (CheckPicker): no "All" entry — an empty selection means all.
// Every distribution value a row can carry MUST appear here: applyFiltersToTree
// drops any row the selection cannot match, so a missing option removes those
// rows from the tree with no error, no toast and no result count.
export const DISTRIBUTION_FILTER_OPTIONS = [
  { label: "Community", value: DISTRIBUTION_FILTER.COMMUNITY },
  { label: "Direct", value: DISTRIBUTION_FILTER.DIRECT },
  { label: "Exclusive", value: DISTRIBUTION_FILTER.EXCLUSIVE },
  { label: PREBUY_LABEL, value: DISTRIBUTION_FILTER.PREBUY },
];

// Keyed off DISTRIBUTION, not COMMERCIAL_MODEL: this map labels a distribution
// value. The three original keys are byte-identical strings, so re-keying them
// is a no-op — it just stops PREBUY having to be written as a raw literal
// alongside three symbolic siblings.
export const DISTRIBUTION_LABELS = {
  [DISTRIBUTION.COMMUNITY]: "Community",
  [DISTRIBUTION.DIRECT]: "Direct",
  [DISTRIBUTION.EXCLUSIVE]: "Exclusive",
  [DISTRIBUTION.PREBUY]: PREBUY_LABEL,
};

export const COPY = {
  PAGE_TITLE: "Rate Plans",
  SEARCH_PLACEHOLDER: "Search Rate Plans",
  SEARCH_ARIA_LABEL: "Search Rate Plans",
  LOADING: "Loading rate plans...",
  EMPTY: "No rate plans found.",
  ERROR_LOAD_FAILED: "Failed to load rate plans. Please try again later.",
  ERROR_EXPORT_FAILED: "Failed to export rate plans. Please try again.",
};

export const SEARCH_DEBOUNCE_MS = 500;

// Maps SP existing `clientType` API filter values to the new distribution filter.
// The existing endpoint only knows "all" / "exclusive" / "non_exclusive". For the
// new Community/Direct filters we send "non_exclusive" and rely on response-side
// distinction via `commercialModel` (if present) — falls back to client-side filtering
// if backend doesn't honor the model split.
export const DISTRIBUTION_TO_CLIENT_TYPE = {
  [DISTRIBUTION_FILTER.ALL]: "all",
  [DISTRIBUTION_FILTER.EXCLUSIVE]: "exclusive",
  [DISTRIBUTION_FILTER.COMMUNITY]: "non_exclusive",
  [DISTRIBUTION_FILTER.DIRECT]: "non_exclusive",
  // Symmetry only — this map is currently unused. Present so a later wiring
  // cannot emit clientType=undefined for a Prebuy selection.
  [DISTRIBUTION_FILTER.PREBUY]: "all",
};
